import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import Loader from "../../elements/Loader";

const STATUS_CONFIG = {
  DRAFT:               { label: "Draft",               className: "badge-secondary" },
  UNDER_REVIEW:        { label: "Under Review",        className: "badge-warning"   },
  CORRECTION_REQUIRED: { label: "Correction Required", className: "badge-danger"    },
  APPROVED:            { label: "Approved",            className: "badge-success"   },
  REJECTED:            { label: "Rejected",            className: "badge-dark"      },
  ACTIVE:              { label: "Active",              className: "badge-primary"   },
  SUSPENDED:           { label: "Suspended",           className: "badge-danger"    },
};

const DOC_STATUS_CONFIG = {
  PENDING:  { label: "Pending",  className: "badge-warning" },
  APPROVED: { label: "Approved", className: "badge-success" },
  REJECTED: { label: "Rejected", className: "badge-danger"  },
};

const DOCUMENTS = [
  { key: "aadhaar",     label: "Aadhaar Card",      urlField: "aadharUrl"        },
  { key: "pan",         label: "PAN Card",           urlField: "panUrl"           },
  { key: "certificate", label: "Priest Certificate", urlField: "certificatePdfUrl"},
];

function Section({ title, children }) {
  return (
    <div className="card mb-2" style={{ borderRadius: 6, border: "1px solid #e9ecef" }}>
      <div style={{ padding: "7px 12px", borderBottom: "1px solid #f0f0f0", background: "#fafafa", borderRadius: "6px 6px 0 0" }}>
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#344050" }}>{title}</span>
      </div>
      <div style={{ padding: "10px 12px" }}>{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="col-md-4" style={{ marginBottom: 8 }}>
      <p className="mb-0" style={{ fontSize: "10px", textTransform: "uppercase", color: "#9aa0ac", letterSpacing: "0.4px" }}>
        {label}
      </p>
      <p className="mb-0" style={{ fontSize: "13px", color: "#344050" }}>{value || "—"}</p>
    </div>
  );
}

export default function PanditDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [pandit, setPandit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [docStatuses, setDocStatuses] = useState({});
  const [docRemarks, setDocRemarks] = useState({});
  const [overallRemark, setOverallRemark] = useState("");
  const [newStatus, setNewStatus] = useState("");

  useEffect(() => {
    ApiService.get(`pandit-verification/${userId}`)
      .then((data) => {
        setLoading(false);
        if (data?.result) {
          const p = data.result;
          setPandit(p);
          setDocStatuses(p.documentStatuses || {});
          setDocRemarks(p.documentRemarks || {});
          setNewStatus(p.applicationStatus || "UNDER_REVIEW");
        }
      })
      .catch(() => {
        setLoading(false);
        toast.error("Failed to load pandit details");
      });
  }, [userId]);

  const handleDocStatus = (docKey, status) => {
    setDocStatuses((prev) => ({ ...prev, [docKey]: status }));
  };

  const handleDocRemark = (docKey, remark) => {
    setDocRemarks((prev) => ({ ...prev, [docKey]: remark }));
  };

  const handleSubmitReview = () => {
    if (!newStatus) return toast.error("Please select an application status");
    setSubmitting(true);
    ApiService.put(`pandit-verification/${userId}/review`, {
      applicationStatus: newStatus,
      documentStatuses: docStatuses,
      documentRemarks: docRemarks,
      overallRemark,
    })
      .then(() => {
        setSubmitting(false);
        toast.success("Review submitted successfully");
        navigate("/pandit-verification");
      })
      .catch(() => {
        setSubmitting(false);
        toast.error("Failed to submit review");
      });
  };

  if (loading) return <Loader />;
  if (!pandit) return <div className="text-center py-5">Pandit not found</div>;

  const user = pandit.user || {};
  const appStatus = pandit.applicationStatus || "DRAFT";
  const statusCfg = STATUS_CONFIG[appStatus] || STATUS_CONFIG.DRAFT;

  return (
    <div className="row">
      <div className="col-xl-12">

        {/* Header */}
        <div className="card mb-2" style={{ borderRadius: 6, border: "1px solid #e9ecef" }}>
          <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {user.profileImage ? (
                <img src={user.profileImage} alt="profile"
                  style={{ width: 42, height: 42, borderRadius: "50%", objectFit: "cover" }} />
              ) : (
                <div style={{
                  width: 42, height: 42, borderRadius: "50%", background: "#e9ecef",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 18, color: "#6c757d", fontWeight: "bold"
                }}>
                  {user.name?.charAt(0)?.toUpperCase() || "P"}
                </div>
              )}
              <div>
                <p className="mb-0" style={{ fontSize: "14px", fontWeight: 600, color: "#344050" }}>{user.name || "—"}</p>
                <p className="mb-0" style={{ fontSize: "12px", color: "#9aa0ac" }}>{user.phone}{user.email ? ` · ${user.email}` : ""}</p>
              </div>
              <span className={`badge light ${statusCfg.className}`} style={{ fontSize: "11px", marginLeft: 8 }}>
                {statusCfg.label}
              </span>
            </div>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>← Back</button>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-8">

            {/* Personal Details */}
            <Section title="Personal Details">
              <div className="row">
                <Field label="Full Name"    value={user.name} />
                <Field label="Phone"        value={user.phone} />
                <Field label="Email"        value={user.email} />
                <Field label="Date of Birth" value={pandit.dob} />
                <Field label="Gender"       value={pandit.gender} />
                <Field label="Father's Name" value={pandit.fatherName} />
              </div>
            </Section>

            {/* Address */}
            <Section title="Address">
              <div className="row">
                <Field label="Current Address" value={pandit.currentAddress} />
                <Field label="Landmark"        value={pandit.landmark} />
                <Field label="City"            value={pandit.city} />
                <Field label="State"           value={pandit.state} />
                <Field label="Pincode"         value={pandit.pincode} />
              </div>
            </Section>

            {/* Professional Details */}
            <Section title="Professional Details">
              <div className="row">
                <Field label="Tradition"             value={pandit.tradition} />
                <Field label="Years of Experience"   value={pandit.yearsExperience !== null ? `${pandit.yearsExperience} years` : null} />
                <Field label="Approx Pujas Performed" value={pandit.approxPujasPerformed} />
                <Field label="Lineage Details"       value={pandit.lineageDetails} />
                <Field label="Languages"             value={(pandit.languages || []).join(", ")} />
                <Field label="Service Mode"          value={pandit.mode} />
              </div>
            </Section>

            {/* Expertise */}
            <Section title="Expertise">
              <p className="mb-1 text-muted" style={{ fontSize: "10px", textTransform: "uppercase" }}>Puja Types</p>
              <div className="d-flex flex-wrap gap-1">
                {(pandit.pujaTypes || []).map((pt) => (
                  <span key={pt} className="badge light badge-primary" style={{ fontSize: "12px", padding: "4px 8px" }}>{pt}</span>
                ))}
                {(!pandit.pujaTypes || pandit.pujaTypes.length === 0) && <span className="text-muted">—</span>}
              </div>
            </Section>

            {/* Service Location */}
            <Section title="Service Location">
              <div className="row">
                <Field label="Base City"      value={pandit.city} />
                <Field label="Base Pincode"   value={pandit.basePincode} />
                <Field label="Service Radius" value={pandit.serviceRadiusKm ? `${pandit.serviceRadiusKm} km` : null} />
                <Field label="Latitude"       value={pandit.baseLat != null ? String(pandit.baseLat) : null} />
                <Field label="Longitude"      value={pandit.baseLng != null ? String(pandit.baseLng) : null} />
              </div>
              <p className="mb-1 text-muted mt-1" style={{ fontSize: "10px", textTransform: "uppercase" }}>Serviceable Cities</p>
              <div className="d-flex flex-wrap gap-1">
                {(pandit.serviceableCities || []).map((c) => (
                  <span key={c} className="badge light badge-info" style={{ fontSize: "12px", padding: "4px 8px" }}>{c}</span>
                ))}
                {(!pandit.serviceableCities || pandit.serviceableCities.length === 0) && <span className="text-muted">—</span>}
              </div>
            </Section>

            {/* Bank Details */}
            <Section title="Bank Details">
              <div className="row">
                <Field label="Account Holder Name" value={pandit.accountHolderName} />
                <Field label="Account Number"      value={pandit.accountNumber} />
                <Field label="IFSC Code"           value={pandit.ifscCode} />
                <Field label="Bank Name"           value={pandit.bankName} />
              </div>
            </Section>

          </div>

          <div className="col-xl-4">

            {/* Documents */}
            <div className="card mb-2" style={{ borderRadius: 6, border: "1px solid #e9ecef" }}>
              <div style={{ padding: "7px 12px", borderBottom: "1px solid #f0f0f0", background: "#fafafa", borderRadius: "6px 6px 0 0" }}>
                <span style={{ fontSize: "12px", fontWeight: 600, color: "#344050" }}>Documents</span>
              </div>
              <div style={{ padding: "10px 12px" }}>

                {/* Profile Photo */}
                <div className="mb-2 pb-2" style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <p className="mb-1 text-muted" style={{ fontSize: "10px", textTransform: "uppercase" }}>Profile Photo</p>
                  {user.profileImage ? (
                    <a href={user.profileImage} target="_blank" rel="noopener noreferrer">
                      <img src={user.profileImage} alt="profile"
                        style={{ width: "100%", maxHeight: 120, objectFit: "cover", borderRadius: 6, border: "1px solid #dee2e6" }} />
                    </a>
                  ) : <p className="text-muted mb-0" style={{ fontSize: "12px" }}>Not uploaded</p>}
                </div>

                {DOCUMENTS.map((doc) => {
                  const url = pandit[doc.urlField];
                  const docStatus = docStatuses[doc.key] || "PENDING";
                  const dsCfg = DOC_STATUS_CONFIG[docStatus] || DOC_STATUS_CONFIG.PENDING;
                  const isPdf = url && url.toLowerCase().includes(".pdf");

                  return (
                    <div key={doc.key} className="mb-2 pb-2" style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <p className="mb-0" style={{ fontSize: "12px", fontWeight: 600 }}>{doc.label}</p>
                        <span className={`badge light ${dsCfg.className}`} style={{ fontSize: "10px" }}>{dsCfg.label}</span>
                      </div>

                      {url ? (
                        isPdf ? (
                          <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary btn-sm w-100 mb-1">
                            <i className="fa fa-file-pdf-o me-1" /> View PDF
                          </a>
                        ) : (
                          <a href={url} target="_blank" rel="noopener noreferrer">
                            <img src={url} alt={doc.label}
                              style={{ width: "100%", maxHeight: 110, objectFit: "cover", borderRadius: 5, border: "1px solid #dee2e6", marginBottom: 6 }} />
                          </a>
                        )
                      ) : (
                        <p className="text-muted mb-1" style={{ fontSize: "11px" }}>Not uploaded</p>
                      )}

                      <div className="d-flex gap-1 mb-1">
                        <button
                          className={`btn btn-xs ${docStatus === "APPROVED" ? "btn-success" : "btn-outline-success"} flex-fill`}
                          onClick={() => handleDocStatus(doc.key, "APPROVED")}
                        >Approve</button>
                        <button
                          className={`btn btn-xs ${docStatus === "REJECTED" ? "btn-danger" : "btn-outline-danger"} flex-fill`}
                          onClick={() => handleDocStatus(doc.key, "REJECTED")}
                        >Reject</button>
                      </div>

                      {docStatus === "REJECTED" && (
                        <input type="text" className="form-control form-control-sm"
                          placeholder="Reason for rejection..."
                          value={docRemarks[doc.key] || ""}
                          onChange={(e) => handleDocRemark(doc.key, e.target.value)} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Review Action Panel */}
            <div className="card mb-2" style={{ borderRadius: 6, border: "1px solid #e9ecef" }}>
              <div style={{ padding: "7px 12px", borderBottom: "1px solid #f0f0f0", background: "#fafafa", borderRadius: "6px 6px 0 0" }}>
                <span style={{ fontSize: "12px", fontWeight: 600, color: "#344050" }}>Review Decision</span>
              </div>
              <div style={{ padding: "10px 12px" }}>
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: "11px", textTransform: "uppercase", color: "#6c757d" }}>
                    Application Status
                  </label>
                  <select className="form-select form-select-sm" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                    {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                      <option key={val} value={val}>{cfg.label}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: "11px", textTransform: "uppercase", color: "#6c757d" }}>
                    Overall Remark (optional)
                  </label>
                  <textarea className="form-control form-control-sm" rows={2}
                    placeholder="Add a remark for the pandit..."
                    value={overallRemark}
                    onChange={(e) => setOverallRemark(e.target.value)} />
                </div>

                <button className="btn btn-primary btn-sm w-100" onClick={handleSubmitReview} disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit Review"}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
